import { Clock, CreditCard, Gift, Search, Shield, Users,Power } from "lucide-react";
import React, { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";
import useUsers from "../../store/users";
import Button from "../common/Button";

// AnalyticCard Component (Your existing implementation)
const AnalyticCard = ({ title, value, icon: Icon, trend, color, subtitle }) => (
  <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-100">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-sm text-gray-500 mb-1">{title}</p>
        <h3 className="text-2xl font-semibold">{value}</h3> 
        {subtitle && <p className="text-sm text-gray-500 mt-1">{subtitle}</p>}
        {trend && (
          <p
            className={`text-sm mt-2 ${
              trend > 0 ? "text-green-500" : "text-red-500"
            }`}
          >
            {trend > 0 ? "↑" : "↓"} {Math.abs(trend)}% from last month
          </p>
        )}
      </div>
      <div className={`p-3 rounded-full ${color}`}>
        <Icon className="h-6 w-6 text-white" />
      </div>
    </div>
  </div>
);

// Enhanced CreditsDistribution Component
const CreditsDistribution = ({ users }) => {
  const getCreditRanges = () => {
    const ranges = {
      0: 0,
      "1-10": 0,
      "11-50": 0,
      "51-100": 0,
      "101-500": 0,
      "500+": 0,
    };

    users.forEach((user) => {
      const credits = user.credits || 0;
      if (credits === 0) ranges["0"]++;
      else if (credits <= 10) ranges["1-10"]++;
      else if (credits <= 50) ranges["11-50"]++;
      else if (credits <= 100) ranges["51-100"]++;
      else if (credits <= 500) ranges["101-500"]++;
      else ranges["500+"]++;
    });

    return Object.entries(ranges).map(([range, count]) => ({
      range,
      count,
      percentage: ((count / users.length) * 100).toFixed(1),
    }));
  };

  const data = getCreditRanges();
  const colors = {
    0: "bg-red-600",
    "1-10": "bg-orange-500",
    "11-50": "bg-yellow-500",
    "51-100": "bg-green-500",
    "101-500": "bg-blue-500",
    "500+": "bg-purple-500",
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold">Credits Distribution</h3>
      </div>
      <div className="space-y-4">
        {data.map(({ range, count, percentage }) => (
          <div key={range} className="relative">
            <div className="flex justify-between mb-1">
              <div>
                <span className="text-sm font-medium">{range} credits</span>
                <span className="text-sm text-gray-500 ml-2">
                  ({count} users)
                </span>
              </div>
              <span className="text-sm text-gray-500">{percentage}%</span>
            </div>
            <div className="w-full bg-gray-100 rounded-full h-2.5">
              <div
                className={`h-2.5 rounded-full ${colors[range]}`}
                style={{ width: `${percentage}%` }}
              ></div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// RecentUsers Component
const RecentUsers = ({ users }) => (
  <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100">
    <div className="flex items-center justify-between mb-4">
      <h3 className="font-semibold">Recent Users</h3>
      <Link
        to={"/users"}
        className="text-indigo-600 text-sm hover:text-indigo-800"
      >
        View all
      </Link>
    </div>
    <div className="space-y-4">
      {users.slice(0, 5).map((user, index) => (
        <div key={index} className="flex items-center justify-between py-2">
          <div className="flex items-center space-x-3">
            <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
              <Users className="h-5 w-5 text-gray-500" />
            </div>
            <div>
              <p className="font-medium">{user.email?.split("@")[0]}</p>
              <p className="text-sm text-gray-500">{user.email}</p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-sm font-medium">{user.credits || 0} credits</p>
            <p className="text-sm text-gray-500">
              {user?.lastReferralRewardDate
                ? "Recent Reffered at " +
                  user?.lastReferralRewardDate?.toDate()?.toLocaleDateString()
                : "No Refferal activity"}
            </p>
          </div>
        </div>
      ))}
    </div>
  </div>
);

// ReferralStats Component
const ReferralStats = ({ referralStats }) => {
  const topReferrers = Object.entries(referralStats)
    .map(([code, stats]) => ({
      code,
      ...stats,
    }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 5);
    
      
  return (
    <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100">
      <h3 className="font-semibold mb-4">Top Referral Codes</h3>
      <div className="space-y-4">
        {topReferrers.length ? (
          topReferrers.map((referrer) => (
            <div
              key={referrer.code}
              className="flex items-center justify-between"
            >
              <div>
                <p className="font-medium">{referrer.user }<span className="text-sm"> ({referrer.code}) </span></p>
                <p className="text-sm text-gray-500">Invited {referrer.count} colleagues

                </p>
              </div>
              <div className="text-right">
                <p className="font-medium">{referrer.totalCredits} credits</p>
                <p className="text-sm text-gray-500">
                  {referrer.activeUsers} active
                </p>
              </div>
            </div>
          ))
        ) : (
          <div className="flex items-center justify-center h-24">
            <p className="text-gray-500">No referral codes found</p>
          </div>
        )}
      </div>
    </div>
  );
};

// DomainDistribution Component
const DomainDistribution = ({ domainStats }) => {
  const data = Object.entries(domainStats)
    .map(([domain, count]) => ({
      domain,
      count,
      percentage: count,
    }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 5);

  const COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042", "#8884d8"];

  // Custom label renderer
  const renderCustomizedLabel = ({
    cx,
    cy,
    midAngle,
    innerRadius,
    outerRadius,
    percent,
    index,
    domain,
  }) => {
    const RADIAN = Math.PI / 180;
    // Extend the radius for label placement
    const radius = outerRadius * 1.2;

    // Calculate label position
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    // Calculate anchor based on position
    const textAnchor = x > cx ? "start" : "end";

    // Format domain name to be more readable
    const formattedDomain =
      domain.length > 15 ? `${domain.substring(0, 15)}...` : domain;

    return (
      <text
        x={x}
        y={y}
        textAnchor={textAnchor}
        fill="#333333"
        dominantBaseline="central"
        fontSize="12"
      >
        {`${formattedDomain} (${(percent * 100).toFixed(0)}%)`}
      </text>
    );
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100">
      <h3 className="font-semibold mb-4">Email Domain Distribution</h3>
      <div className="h-72">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={data}
              dataKey="count"
              nameKey="domain"
              cx="50%"
              cy="50%"
              outerRadius={80}
              fill="#8884d8"
              labelLine={true}
              label={renderCustomizedLabel}
            >
              {data.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={COLORS[index % COLORS.length]}
                />
              ))}
            </Pie>
            <Tooltip
              formatter={(value, name, props) => [
                `${value} users`,
                props.payload.domain,
              ]}
              contentStyle={{ fontSize: "12px" }}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>

      {/* Legend below the chart */}
      <div className="mt-4 grid grid-cols-1 gap-2">
        {data.map((entry, index) => (
          <div
            key={`legend-${index}`}
            className="flex items-center justify-between"
          >
            <div className="flex items-center">
              <div
                className="w-3 h-3 rounded-full mr-2"
                style={{ backgroundColor: COLORS[index % COLORS.length] }}
              />
              <span className="text-sm">{entry.domain}</span>
            </div>
            <span className="text-sm text-gray-600">{entry.count} users</span>
          </div>
        ))}
      </div>
    </div>
  );
};
// Main Dashboard Component
function Dashboard() {

  const { users, analytics,  fetchUsers, calculateAnalytics } =
    useUsers();
  const navigate = useNavigate();

  useEffect(() => {
    // fetchUsers();
    // calculateAnalytics();
   
    // use promise to calcualte analytics after fetching users
    fetchUsers().then(() => calculateAnalytics());
  }, [fetchUsers]);

  

  return (
    <div className="min-h-screen bg-gray-50 p-6">
  
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
          <Button
            leftIcon={<Users className="h-4 w-4" />}
            onClick={() => navigate("/users")}
          >
            <span>Manage Users</span>
          </Button>
        </div>

        {/* Primary Analytics Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <AnalyticCard
            title="Total Users"
            value={analytics.totalUsers}
            icon={Users}
            color="bg-blue-500"
          />
           <AnalyticCard
            title="Total Admins"
            value={analytics.totalAdmins}
            icon={Power}
            color="bg-blue-500"
          />
          <AnalyticCard
            title="Total Credits"
            value={analytics.totalCredits.toLocaleString()}
            icon={CreditCard}
            subtitle={`Avg ${analytics.avgCreditsPerUser} per user`}
            color="bg-green-500"
          />

          <AnalyticCard
            title="Total Searches"
            value={analytics.totalSearches}
            icon={Search}
            subtitle={`Avg ${analytics.avgSearchesPerUser} per user`}
            color="bg-orange-500"
          />
         
        </div>

        {/* Secondary Analytics Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <AnalyticCard
            title="Referred Users"
            value={analytics.referredUsers}
            icon={Users}
            subtitle={`${(
              (analytics.referredUsers / analytics.totalUsers) *
              100
            ).toFixed(1)}% of total users`}
            color="bg-purple-500"
          />

          {/* Secondary Analytics Grid */}
          <AnalyticCard
            title="Successful Referrers"
            value={analytics.successfulReferrers}
            icon={Gift}
            subtitle={`${(
              (analytics.successfulReferrers / analytics.totalUsers) *
              100
            ).toFixed(1)}% conversion`}
            color="bg-indigo-500"
          />

          <AnalyticCard
            title="Last Active Users"
            value={analytics.lastActiveUsers}
            icon={Clock}
            color="bg-teal-500"
          />
           <AnalyticCard
            title="Lifetime Users"
            value={analytics.lifetimeUsers}
            icon={Shield}
            subtitle={`${Math.round(
              (analytics.lifetimeUsers / analytics.totalUsers) * 100
            )}% of total`}
            color="bg-yellow-500"
          />
        </div>

        {/* Charts and Stats Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-stretch">
          <DomainDistribution domainStats={analytics.domainStats} />

          <ReferralStats referralStats={analytics.referralStats} />

          <CreditsDistribution users={users} />
        </div>

        {/* Recent Users Section */}
        <div className="mt-8">
          <RecentUsers users={users} />
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
